import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react';

const ComboboxBase = forwardRef(
    (
        {
            label,
            name,
            autocomplete,
            error,
            disableTab,
            hint,

            value,
            onInputChange, // (event) => void
            onSelectOption, // (item) => void

            items = [],
            isLoading = false,

            isOpen,
            setIsOpen,

            // option helpers
            getOptionKey,
            getOptionLabel,
            getOptionSubLabel,

            // meta renderer
            renderMeta, // ({ value, items, isLoading }) => ReactNode

            // visuals
            icon, // <FontAwesomeIcon ... />
            inputProps = {},
            ...props
        },
        ref
    ) => {
        const reactId = useId();
        const inputId = useMemo(() => name || `combobox-${reactId}`, [name, reactId]);
        const listboxId = `${inputId}-listbox`;
        const errorId = `${inputId}-error`;

        const rootRef = useRef(null);
        const [showTooltip, setShowTooltip] = useState(false);
        const [activeIndex, setActiveIndex] = useState(-1);

        const isFilled = !!(value || '').trim();

        const open = () => {
            if (!disableTab) setIsOpen(true);
        };

        const close = () => {
            setIsOpen(false);
            setActiveIndex(-1);
        };

        // Click outside closes
        useEffect(() => {
            if (!isOpen) return;

            const onDocMouseDown = e => {
                if (!rootRef.current) return;
                if (!rootRef.current.contains(e.target)) close();
            };

            document.addEventListener('mousedown', onDocMouseDown);
            return () => document.removeEventListener('mousedown', onDocMouseDown);
        }, [isOpen]);

        const handleKeyDown = e => {
            if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                open();
                return;
            }

            if (!isOpen) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                close();
                return;
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(prev => (prev + 1 >= items.length ? 0 : prev + 1));
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => (prev - 1 < 0 ? items.length - 1 : prev - 1));
                return;
            }

            if (e.key === 'Enter') {
                if (activeIndex >= 0 && items[activeIndex]) {
                    e.preventDefault();
                    onSelectOption?.(items[activeIndex]);
                    close();
                }
            }
        };

        const activeDescendant = activeIndex >= 0 ? `${inputId}-opt-${activeIndex}` : undefined;

        const toggleTooltip = () => setShowTooltip(prev => !prev);

        return (
            <div ref={rootRef} className={`form-group ${error ? 'has-error' : ''}`}>
                <div className={`field field--combobox ${isFilled ? 'is-filled' : ''}`}>
                    <input
                        id={inputId}
                        name={name}
                        ref={ref}
                        type="text"
                        autoComplete={autocomplete}
                        placeholder=" "
                        tabIndex={disableTab ? -1 : 0}
                        aria-invalid={!!error}
                        aria-describedby={error ? errorId : undefined}
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={isOpen}
                        aria-controls={listboxId}
                        aria-activedescendant={activeDescendant}
                        value={value || ''}
                        onChange={onInputChange}
                        onFocus={open}
                        onBlur={() => close()} // fokus flyttes => lukk
                        onKeyDown={handleKeyDown}
                        {...inputProps}
                        {...props}
                    />

                    <label htmlFor={inputId}>{label}</label>

                    {icon ? <span className="combobox-icon">{icon}</span> : null}

                    {error && (
                        <>
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                className="error-icon"
                                size="md"
                                onClick={toggleTooltip}
                                aria-controls={errorId}
                                aria-expanded={showTooltip}
                            />
                            {showTooltip && (
                                <div id={errorId} className="field-error-tooltip" role="alert">
                                    {error.message}
                                    <span className="field-error-tooltip__arrow" />
                                </div>
                            )}
                        </>
                    )}

                    {isOpen && (
                        <div className="combobox-popover">
                            <div className="combobox-meta">
                                {renderMeta?.({ value, items, isLoading })}
                            </div>

                            <ul id={listboxId} role="listbox" className="combobox-list">
                                {items.map((opt, idx) => {
                                    const isActive = idx === activeIndex;
                                    const sub = getOptionSubLabel?.(opt);

                                    return (
                                        <li
                                            key={getOptionKey(opt)}
                                            id={`${inputId}-opt-${idx}`}
                                            role="option"
                                            aria-selected={isActive}
                                            className={`combobox-option ${isActive ? 'is-active' : ''}`}
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={() => {
                                                onSelectOption?.(opt);
                                                close();
                                            }}
                                            onMouseEnter={() => setActiveIndex(idx)}
                                        >
                                            <div className="combobox-option__main">
                                                {getOptionLabel(opt)}
                                            </div>
                                            {sub ? (
                                                <div className="combobox-option__sub">{sub}</div>
                                            ) : null}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>

                {hint && <span className="field-hint">{hint}</span>}
            </div>
        );
    }
);

ComboboxBase.displayName = 'ComboboxBase';
export default ComboboxBase;
